import * as React from "react"
import TextInput, { ITextInputProps } from "../components/TextInput"
import TextAreaInput from "../components/TextInput/TextAreaInput"
import { isRequired, checkNumbers, checkSize } from "../utils/validators"
import PrimaryButton from "../components/PrimaryButton"
import { ReducerState } from "AppTypes"
import { useSelector, useDispatch } from "react-redux"
import { appActions, userInfoActions } from "../actions"
import { IAddressSG, IUserInfo } from "../types/appDataTypes"
import api from "../utils/api"
import getStepConfig from "./stepConfig"
import { digitsOnlyFilter } from "../utils/index"
import { updateLanguageAATagging, updateAATagging } from "../utils/aatagging"

const AddressInfo: React.FC = () => {
    const dispatch = useDispatch()
    const { userInfo, isCallingApi, retrievedAddress, langPack } = useSelector((store: ReducerState) => (
        {
            userInfo: store.userInfo,
            isCallingApi: store.app.isCallingApi,
            retrievedAddress: store.app.retrievedAddress,
            langPack: store.config.langPack
        }
    ))
    const { activeCountry, addressInfo = {} } = userInfo

    const [isAddressRetrieved, setAddressRetrieved] = React.useState(
        !!(addressInfo as IAddressSG).postalCode ? true : false
    )

    const cluster: ITextInputProps["cluster"] = {}

    const nextStepHandler = async () => {
        let isAllValid = true
        for (let key in cluster) {
            if (!cluster[key]()) isAllValid = false
        }
        if (isAllValid) {
            const response = await api.updateApplication(userInfo)
            if (response && response.status === 200) {
                const { nextStep } = getStepConfig()
                dispatch(appActions.gotoStep(nextStep))

                // AA Tracking
                const AALang = updateLanguageAATagging(langPack["lang"])
                updateAATagging({
                    "event_name": "generic_form_complete_step4",
                    "form_name": "UOB PoY",
                    "step_name": "mailing_address",
                    "language": AALang,
                    "country": userInfo.activeCountry.toLowerCase()
                }, "other_form_step")
            }
        }
    }

    const singaporeFields = () => {
        return <React.Fragment>
            <TextInput
                inputLabel={langPack["labelPostalCode"]}
                initValue={(addressInfo as IAddressSG).postalCode}
                inputId={"postalCode"}
                validators={[isRequired, checkNumbers, checkSize(6)]}
                cluster={cluster}
                onValidation={handleUpdateUserInfo}
                maxLength={6}
                type={"tel"}
                inputMode={"numeric"}
                typingHandler={digitsOnlyFilter}
                disabled={isCallingApi}
            />
            {(isAddressRetrieved || (addressInfo as IAddressSG).street) && <React.Fragment>
                <TextInput
                    inputLabel={"Street"}
                    initValue={(addressInfo as IAddressSG).street}
                    inputId={"street"}
                    validators={[isRequired]}
                    cluster={cluster}
                    onValidation={handleUpdateUserInfo}
                    className={isAddressRetrieved ? "app-field-fade-in" : ""}
                />
                <TextInput
                    inputLabel={"Block"}
                    initValue={(addressInfo as IAddressSG).block}
                    inputId={"block"}
                    validators={[isRequired]}
                    cluster={cluster}
                    onValidation={handleUpdateUserInfo}
                    className={isAddressRetrieved ? "app-field-fade-in" : ""}
                />
                <TextInput
                    inputLabel={"Level"}
                    initValue={(addressInfo as IAddressSG).level}
                    inputId={"level"}
                    validators={[]}
                    cluster={cluster}
                    onValidation={handleUpdateUserInfo}
                    className={isAddressRetrieved ? "app-field-fade-in" : ""}
                />
                <TextInput
                    inputLabel={"Unit No."}
                    initValue={(addressInfo as IAddressSG).unitNo}
                    inputId={"unitNo"}
                    validators={[]}
                    cluster={cluster}
                    onValidation={handleUpdateUserInfo}
                    className={isAddressRetrieved ? "app-field-fade-in" : ""}
                />
                {(retrievedAddress && retrievedAddress.addressFormat === "H") || <TextInput
                    inputLabel={"Building Name"}
                    initValue={(addressInfo as IAddressSG).buildingName}
                    inputId={"buildingName"}
                    validators={[]}
                    cluster={cluster}
                    onValidation={handleUpdateUserInfo}
                    className={isAddressRetrieved ? "app-field-fade-in" : ""}
                />}
            </React.Fragment>}
        </React.Fragment>
    }

    const otherCountryFields = (country: IUserInfo['activeCountry']) => {
        const fields = [1, 2]
        return <React.Fragment>
            {fields.map((seq) => <TextAreaInput
                key={"line" + seq}
                inputLabel={langPack["labelAddressLine" + seq]}
                initValue={(addressInfo as any)["line" + seq]}
                inputId={"line" + seq}
                validators={[isRequired]}
                cluster={cluster}
                onValidation={handleUpdateUserInfo}
            />)}
        </React.Fragment>
    }

    const handleUpdateUserInfo: ITextInputProps["onValidation"] = async (validation) => {
        const { isValid, inputId, value } = validation

        if (isValid) {
            if (inputId === "postalCode") {
                if (userInfo.addressInfo && value !== (userInfo.addressInfo as IAddressSG)["postalCode"]) {
                    setAddressRetrieved(false)
                    dispatch(userInfoActions.updateUserInfo({ addressInfo: {} }))
                    const addressRes = await api.getAddress(value)
                    if (addressRes && addressRes.data) {
                        const { data } = addressRes
                        dispatch(appActions.updateRetrievedAddress(data))
                        dispatch(userInfoActions.updateUserInfo({
                            addressInfo: {
                                postalCode: data.postalCode,
                                street: data.streetAddress,
                                block: data.blockNumber,
                                buildingName: data.buildingName
                            }
                        }))
                    }
                    setAddressRetrieved(true)
                }
            }

            dispatch(userInfoActions.updateAddressInfo({ [inputId]: value }))
        } else {

            if (inputId === "postalCode") {
                dispatch(appActions.updateRetrievedAddress(null))
            }
            dispatch(userInfoActions.updateAddressInfo({ [inputId]: "" }))
        }
    }

    return (
        <React.Fragment>
            <div className="content content--form">
                <div>
                    <h2 className="app-title">{langPack["labelMailingAddress"]}</h2>
                    {/* <p className="app-sub-title">{langPack["labelAddress"]}</p> */}
                </div>
                {activeCountry === "SG"
                    ? singaporeFields()
                    : otherCountryFields(activeCountry)
                }

            </div>
            <div className="action">
                {langPack["competitionDisclaimer"] &&
                    <>
                        <p className="app-disclaimer">{langPack["competitionDisclaimerText"]}</p>
                        <br />
                    </> 
                }
                <PrimaryButton
                    text={langPack["buttonNext"]}
                    onClick={nextStepHandler}
                    disabled={isCallingApi}
                />
            </div>

        </React.Fragment>
    )
}

export default AddressInfo